import { supabase } from '@/integrations/supabase/client';
import { BaseService, ServiceResponse, ServiceOptions } from './BaseService';

export interface CRUDOptions extends ServiceOptions {
  tableName: string;
}

export abstract class CRUDService extends BaseService {
  /**
   * Generic method to get data with optional date filtering
   */
  protected static async getData<T>(
    tableName: string,
    userId: string,
    days?: number,
    options: ServiceOptions = {}
  ): Promise<ServiceResponse<T[]>> {
    return this.handleRequest(async () => {
      let query = (supabase as any)
        .from(tableName)
        .select('*')
        .eq('user_id', userId)
        .order('recorded_date', { ascending: false });

      if (days) {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        query = query.gte('recorded_date', startDate.toISOString().split('T')[0]);
      }

      return await query;
    }, options);
  }

  /**
   * Generic method to get the latest entry
   */
  protected static async getLatest<T>(
    tableName: string,
    userId: string,
    options: ServiceOptions = {}
  ): Promise<ServiceResponse<T | null>> {
    return this.handleRequest(async () => {
      const query = (supabase as any)
        .from(tableName)
        .select('*')
        .eq('user_id', userId)
        .order('recorded_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      return await query;
    }, options);
  }

  /**
   * Generic method to create an entry
   */
  protected static async createEntry<T, TInsert>(
    tableName: string,
    entry: TInsert,
    options: ServiceOptions = {}
  ): Promise<ServiceResponse<T>> {
    return this.handleRequest(async () => {
      const query = (supabase as any)
        .from(tableName)
        .insert(entry)
        .select()
        .single();

      return await query;
    }, options);
  }

  protected static async updateEntry<T>(
    tableName: string,
    id: string,
    updates: Partial<T>,
    options: ServiceOptions = {}
  ): Promise<ServiceResponse<T>> {
    return this.handleRequest(async () => {
      const query = (supabase as any)
        .from(tableName)
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      return await query;
    }, options);
  }

  /**
   * Generic method to delete an entry
   */
  protected static async deleteEntry(
    tableName: string,
    id: string,
    options: ServiceOptions = {}
  ): Promise<ServiceResponse<null>> {
    return this.handleRequest(async () => {
      const query = (supabase as any)
        .from(tableName)
        .delete()
        .eq('id', id);

      return await query;
    }, options);
  }

  protected static async getRecent<T>(
    tableName: string,
    userId: string,
    limit = 10,
    options: ServiceOptions = {}
  ): Promise<ServiceResponse<T[]>> {
    return this.handleRequest(async () => {
      const query = (supabase as any)
        .from(tableName)
        .select('*')
        .eq('user_id', userId)
        .order('recorded_date', { ascending: false })
        .limit(limit);

      return await query;
    }, options);
  }

  protected static async getFiltered<T>(
    tableName: string,
    userId: string,
    filterColumn: string,
    filterValue: any,
    limit = 10,
    options: ServiceOptions = {}
  ): Promise<ServiceResponse<T[]>> {
    return this.handleRequest(async () => {
      const query = (supabase as any)
        .from(tableName)
        .select('*')
        .eq('user_id', userId)
        .eq(filterColumn, filterValue)
        .order('recorded_date', { ascending: false })
        .limit(limit);

      return await query;
    }, options);
  }
}